import {
  DEMO_TEST_PARTNER_TENANT_ID,
  VRP_TENANT_ID,
} from '@vrplatform/ui-common';
import { useContext, useMemo } from 'react';
import { isPlaywrightUserEmail } from './isPlaywrightUserEmail';
import { teamContext, useMe, useTeam } from './team';
import type { Team } from './team';

export function useIsVRTrustPartnerQuery() {
  const [team] = useTeam();
  return team?.type === 'partner' && team.isGL === true;
}

// keeps the returned shape stable while the session team is unresolved
const UNRESOLVED_SUBSCRIPTION: Team['subscription'] = {
  cancelledAt: null,
  status: 'active',
  paymentMethodType: null,
};

export function useBilling() {
  const { team } = useContext(teamContext);
  const { email: userEmail } = useMe();

  const isPlaywright = isPlaywrightUserEmail(userEmail);

  return useMemo(() => {
    const isDirectMarkerTeam =
      !!team && [VRP_TENANT_ID, DEMO_TEST_PARTNER_TENANT_ID].includes(team.id);
    const isDirectCustomer =
      !!team &&
      !!team.billingPartnerId &&
      [VRP_TENANT_ID, DEMO_TEST_PARTNER_TENANT_ID].includes(
        team.billingPartnerId
      ) &&
      !isDirectMarkerTeam;

    const isPlaywrightDemo = !!team?.isTest && isPlaywright;

    const isAccountingPartner =
      !!team &&
      team.type === 'partner' &&
      ![VRP_TENANT_ID, DEMO_TEST_PARTNER_TENANT_ID].includes(team.id);

    const isPartnerClient =
      !!team &&
      team.type === 'propertyManager' &&
      !!team.billingPartnerId &&
      ![VRP_TENANT_ID, DEMO_TEST_PARTNER_TENANT_ID].includes(
        team.billingPartnerId
      );

    // Each customer owns its subscription; the payer controls payment details.
    const showBilling =
      (isDirectCustomer || isAccountingPartner || isPartnerClient) &&
      !!team?.subscription.status;

    const isReadOnly = team?.status === 'inactive' && showBilling;

    return {
      showBilling,
      isReadOnly,
      isPartnerClient,
      subscription: team?.subscription ?? UNRESOLVED_SUBSCRIPTION,
      isDirectCustomer,
      isPlaywrightDemo,
    };
  }, [team, isPlaywright]);
}
